<template>
	<div>
		<h2 class="content-block">Site Settings</h2>
		<div class="content-block">
			<div class="settings-form">
				<DxForm :form-data="form" :col-count="1" label-mode="static">

					<DxSimpleItem data-field="maintenanceMode" :label="{ text: 'Maintenance Mode' }" editor-type="dxSelectBox" :editor-options="{ items: maintenanceOptions, displayExpr: 'text', valueExpr: 'value' }"
						help-text="Enabling maintenance mode will stop users other than the admin from accessing the site."
					/>
					<DxSimpleItem css-class="disable-form-paddings" :visible="form.maintenanceMode === 'on'">
						<div class="dx-error-message reduce-padding">
							<i class="dx-icon-warning"></i>
							<div class="dx-error-message-text">Users besides the admin are not able to access the site.</div>
						</div>
					</DxSimpleItem>

					<DxSimpleItem data-field="defaultRole" :label="{ text: 'Default User Role' }" editor-type="dxSelectBox" :editor-options="{ items: roleOptions, displayExpr: 'text', valueExpr: 'value' }"
						help-text="Newly registered users will be automatically assigned this role."
					/>

					<DxSimpleItem data-field="importMode" :label="{ text: 'Import Mode' }" editor-type="dxSelectBox" :editor-options="{ items: importOptions, displayExpr: 'text', valueExpr: 'value' }"
						help-text="Decide whether new imports wait for review or are directly imported."
					/>

					<DxGroupItem :col-count="2" css-class="buttons">
						<DxButtonItem horizontal-alignment="left" :button-options="{ text: 'Cancel', stylingMode: 'outlined', onClick: handleCancel }" />
						<DxButtonItem :button-options="{ text: 'Save', type: 'default', stylingMode: 'contained', onClick: handleSave }" />
					</DxGroupItem>
					
				</DxForm>
			</div>
		</div>
	</div>
</template>

<style lang="scss">
.settings-form {
	max-width: 640px;
	background: linear-gradient(0deg, rgba(211,47,47,0.03), rgba(211,47,47,0.03)), #fff;
	padding: 18px 24px;
	border-radius: 10px;
	box-shadow: 0 2px 8px rgba(0,0,0,0.12), 0 14px 28px rgba(211,47,47,0.08);
	border: 1px solid #f2b8b5;
	border-left: 4px solid #d32f2f;
}
</style>

<script setup>
import { reactive, ref, onMounted, inject, computed } from 'vue';
import { useRouter } from 'vue-router';
import DxForm, { DxSimpleItem, DxGroupItem, DxButtonItem } from 'devextreme-vue/form';

const $http = inject('$http');
const router = useRouter();

// Roles loaded from backend and exposed to the select box (excluding 'admin')
const roles = ref([]);
const isLoadingRoles = ref(false);
const roleOptions = computed(() => {
	// Map role names to select items, exclude 'admin'; add <none> default
	const items = roles.value
		.filter(name => String(name).toLowerCase() !== 'admin')
		.map(name => ({ value: name, text: name }));
	return [{ value: '', text: '<none>' }, ...items];
});

async function loadRoles() {
	isLoadingRoles.value = true;
	try {
		const response = await $http.get('/role');
		const payload = response && response.data !== undefined ? response.data : response;
		if (Array.isArray(payload)) {
			// Normalize to unique, sorted list of role names
			const names = payload
				.map(r => (r && typeof r === 'object' ? String(r.name ?? '').trim() : ''))
				.filter(Boolean);
			const seen = new Set();
			const unique = [];
			for (const name of names) {
				const key = name.toLowerCase();
				if (seen.has(key)) continue;
				seen.add(key);
				unique.push(name);
			}
			roles.value = unique.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
		} else {
			roles.value = [];
		}
	} catch (e) {
		roles.value = [];
	} finally {
		isLoadingRoles.value = false;
	}
}

const maintenanceOptions = [
	{ value: 'off', text: 'Off' },
	{ value: 'on', text: 'On' }
];

const importOptions = [
	{ value: 'flagged', text: 'Flag imports and wait for review' },
	{ value: 'direct', text: 'Directly import rows without review' },
];

const form = reactive({
	defaultRole: '',
	maintenanceMode: 'off',
	importMode: 'flagged',
});

// Load roles on mount; ensure defaultRole is valid with the loaded options
onMounted(async () => {
	await loadRoles();
	const validValues = new Set(roleOptions.value.map(o => o.value));
	if (!validValues.has(form.defaultRole)) {
		// Default to no role
		form.defaultRole = '';
	}
});

const handleCancel = () => {
	router.push('/');
};

const handleSave = () => {
	// TODO: Persist settings via API
};

</script>
